import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import AshdaleLogoSVG from '../../assets/ashdale-logo.svg';

const { FiMail, FiLock, FiUser, FiEye, FiEyeOff, FiAlertCircle, FiCheckCircle } = FiIcons;

const Register = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [verificationSent, setVerificationSent] = useState(false);

  // Added authLoading to prevent rendering the offline warning while still checking connection
  const { register, supabaseConnected, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Enhanced user-friendly validation
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match. Please ensure both password fields are identical.');
      setLoading(false);
      return;
    }

    if (formData.password.length < 6) {
      setError('Password is too short. Please use at least 6 characters for better security.');
      setLoading(false);
      return;
    }

    try {
      const response = await register(formData.email, formData.password, formData.name);
      
      // If a session exists, the user is logged in automatically
      if (response?.session) {
        navigate('/portfolio');
      } else {
        // If session is null, it means Supabase has Email Confirmation enabled
        setVerificationSent(true);
      }
    } catch (err) {
      setError(err.message || 'Failed to create account. Please check your details and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen bg-black flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="sm:mx-auto sm:w-full sm:max-w-md"
      >
        <div className="text-center">
          <div className="flex justify-center mb-6">
            <img src={AshdaleLogoSVG} alt="Ashdale Analytics" className="h-12" />
          </div>
          <h2 className="text-3xl font-bold text-white">Create your account</h2>
          <p className="mt-2 text-sm text-secondary-400">
            Or{' '}
            <Link to="/login" className="font-medium text-primary-500 hover:text-primary-400">
              sign in to existing account
            </Link>
          </p>
        </div>

        <div className="mt-8 bg-secondary-900 py-8 px-4 shadow-lg sm:rounded-lg sm:px-10 border border-primary-500/20">
          
          {/* Prevent offline warning from flashing while still loading/syncing */}
          {!authLoading && !supabaseConnected && !verificationSent && (
            <div className="mb-4 p-3 bg-yellow-900/20 border border-yellow-500/20 rounded-md flex items-center text-yellow-400">
              <SafeIcon icon={FiAlertCircle} className="h-5 w-5 mr-2" />
              <span className="text-sm">
                Running in offline mode. Some features may be limited.
              </span>
            </div>
          )}

          {verificationSent ? (
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center"
            >
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-900/30 mb-6 border border-green-500/30">
                <SafeIcon icon={FiMail} className="h-8 w-8 text-green-500" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Check your email</h3>
              <p className="text-secondary-400 mb-8">
                We've sent a verification link to <span className="text-white font-medium">{formData.email}</span>.
              </p>
              
              <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-4 text-left mb-8">
                <h4 className="flex items-center font-bold text-yellow-500 mb-2">
                  <SafeIcon icon={FiAlertCircle} className="w-5 h-5 mr-2" />
                  Localhost Error?
                </h4>
                <p className="text-sm text-yellow-400/90 mb-3">
                  If you click the email link and get a <strong>"localhost refused to connect"</strong> error, you need to disable Email Confirmations for your development environment:
                </p>
                <ol className="list-decimal list-inside text-sm text-yellow-500/80 space-y-1">
                  <li>Go to your Supabase Dashboard</li>
                  <li>Click <strong>Authentication</strong> &gt; <strong>Providers</strong> &gt; <strong>Email</strong></li>
                  <li>Toggle <strong>Confirm email</strong> to <strong>OFF</strong></li>
                  <li>Save and try registering again below</li>
                </ol>
              </div>

              <button
                onClick={() => setVerificationSent(false)}
                className="w-full flex justify-center py-2 px-4 border border-secondary-700 rounded-md shadow-sm text-sm font-medium text-white bg-secondary-800 hover:bg-secondary-700 focus:outline-none transition-colors"
              >
                Try registering again
              </button>
            </motion.div>
          ) : (
            <form className="space-y-6" onSubmit={handleSubmit}>
              {error && (
                <div className="bg-red-900/20 border border-red-500/20 text-red-400 px-4 py-3 rounded-md text-sm">
                  {error}
                </div>
              )}

              <div>
                <label htmlFor="name" className="block text-sm font-medium text-white">
                  Full name
                </label>
                <div className="mt-1 relative">
                  <input
                    id="name"
                    name="name"
                    type="text"
                    autoComplete="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="appearance-none block w-full px-3 py-2 pl-10 border border-secondary-700 bg-secondary-800 rounded-md placeholder-secondary-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 text-white"
                    placeholder="Enter your full name"
                  />
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <SafeIcon icon={FiUser} className="h-5 w-5 text-primary-500" />
                  </div>
                </div>
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-white">
                  Email address
                </label>
                <div className="mt-1 relative">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="appearance-none block w-full px-3 py-2 pl-10 border border-secondary-700 bg-secondary-800 rounded-md placeholder-secondary-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 text-white"
                    placeholder="Enter your email"
                  />
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <SafeIcon icon={FiMail} className="h-5 w-5 text-primary-500" />
                  </div>
                </div>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-white">
                  Password
                </label>
                <div className="mt-1 relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    required
                    value={formData.password}
                    onChange={handleChange}
                    className="appearance-none block w-full px-3 py-2 pl-10 pr-10 border border-secondary-700 bg-secondary-800 rounded-md placeholder-secondary-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 text-white"
                    placeholder="Create a password"
                  />
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <SafeIcon icon={FiLock} className="h-5 w-5 text-primary-500" />
                  </div>
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    <SafeIcon icon={showPassword ? FiEyeOff : FiEye} className="h-5 w-5 text-secondary-400 hover:text-primary-500" />
                  </button>
                </div>
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-white">
                  Confirm password
                </label>
                <div className="mt-1 relative">
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    autoComplete="new-password"
                    required
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className="appearance-none block w-full px-3 py-2 pl-10 border border-secondary-700 bg-secondary-800 rounded-md placeholder-secondary-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 text-white"
                    placeholder="Confirm your password"
                  />
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <SafeIcon icon={FiLock} className="h-5 w-5 text-primary-500" />
                  </div>
                </div>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={loading || authLoading}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-black bg-primary-500 hover:bg-primary-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Creating account...' : 'Create account'}
                </button>
              </div>
            </form>
          )}

          {!verificationSent && (
            <div className="mt-6">
              <p className="text-xs text-secondary-500 text-center">
                By creating an account, you agree to our{' '}
                <a href="#" className="text-primary-500 hover:text-primary-400">
                  Terms of Service
                </a>{' '}
                and{' '}
                <a href="#" className="text-primary-500 hover:text-primary-400">
                  Privacy Policy
                </a>
              </p>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default Register;